import { useState, useContext } from 'react';
import {
    Box,
    CircularProgress,
    Link,
    Typography,
    Paper,
    Divider,
} from '@mui/material';
import { PersonOutlineOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import { SttFeeRow, SttNoResults, SttPagination } from '../../../sporttia/all';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { fullName, updateElementInArray } from '../../../lib/utils';
import useGroupsService from '../../../services/GroupsService';
import SttError from '../../../components/error/SttError';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import { Group } from '../../../types/api/models';
import type { Payment } from '../../../types/models/Payment';
import { GetGroupMembers200 } from '../../../types/api/paths/Group';
import { GroupMember, Fee } from '../../../types/models/GroupMember';
import { filtersToApi } from './utils/utils';
import type { Filters } from './utils/utils';

type GroupFeesProps = {
    item: Group;
    filters: Filters;
    onFilterChange: (filters: Filters) => void;
};

/**
 * GroupFees - List of memers' fees
 * Object item - The group object
 */
export default function GroupFees({
    item,
    filters,
    onFilterChange,
}: GroupFeesProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [openMshipDialog, setOpenMshipDialog] = useState(false);
    const [selectedMshipId, setSelectedMshipId] = useState<number>();

    const queryClient = useQueryClient();

    const groupsService = useGroupsService();

    const [getGroupMembersPage, setGetGroupMembersPage] = useState(1);
    const getGroupMembersQuery = groupsService.useGetGroupMembers(item.id, {
        ...filtersToApi({ ...filters, page: getGroupMembersPage, rows: 25 }),
    });

    function updateFeeFromPayment(payment: Payment) {
        queryClient.setQueryData<GetGroupMembers200 | undefined>(
            getGroupMembersQuery.queryKey,
            (old: any) => {
                if (!old) {
                    return undefined;
                }

                return {
                    ...old,
                    rows: old.rows.map((row: any) => {
                        if (payment.user?.id === row.user?.id) {
                            return {
                                ...row,
                                fees: updateElementInArray(row.fees, payment, {
                                    merge: true,
                                }),
                            };
                        }

                        // When a fee is paid for a child, the payment is made by the parent.
                        // Therefore, we search among the children of the payer and add the payment to the child's fees.
                        const foundChildrenPayment =
                            payment.children?.rows?.find(
                                (children) =>
                                    children.user?.id === row.user?.id,
                            );
                        if (row.fees && foundChildrenPayment) {
                            return {
                                ...row,
                                fees: row.fees.map((fee: any) =>
                                    fee.id === foundChildrenPayment.id
                                        ? payment
                                        : fee,
                                ),
                            };
                        }

                        return row;
                    }),
                };
            },
        );
    }

    function deleteFeeFromPayment(payment: Payment) {
        queryClient.setQueryData<GetGroupMembers200 | undefined>(
            getGroupMembersQuery.queryKey,
            (old: any) => {
                if (!old) {
                    return undefined;
                }

                return {
                    ...old,
                    rows: old.rows.map((row: any) => {
                        if (row.fees && row.user?.id === payment.user?.id) {
                            return {
                                ...row,
                                fees: row.fees.map((fee: any) => {
                                    if (fee.id !== payment.id) {
                                        return fee;
                                    }

                                    return {
                                        ...fee,
                                        status: null,
                                        trash: true,
                                    };
                                }),
                            };
                        }

                        // When a fee is paid for a child, the payment is made by the parent.
                        // Therefore, we search among the children of the payer and add the payment to the child's fees.
                        const foundChildrenPayment =
                            payment.children?.rows?.find(
                                (children) =>
                                    children.user?.id === row.user?.id,
                            );
                        if (row.fees && foundChildrenPayment) {
                            return {
                                ...row,
                                fees: row.fees.map((fee: any) =>
                                    fee.id === payment.id
                                        ? {
                                              ...fee,
                                              status: null,
                                              trash: true,
                                          }
                                        : fee,
                                ),
                            };
                        }

                        return row;
                    }),
                };
            },
        );
    }

    function onFeeGenerated(
        updatedFee: Fee,
        indexFee: number,
        selectedMember: GroupMember,
    ) {
        if (
            updatedFee === undefined ||
            indexFee === undefined ||
            selectedMember === undefined
        ) {
            return;
        }

        queryClient.setQueryData<GetGroupMembers200 | undefined>(
            getGroupMembersQuery.queryKey,
            (old: any) => {
                if (!old) {
                    return undefined;
                }

                // Copio los miembros.
                const currentMembers = { ...old };

                // Copio el miembro que voy actualizar.
                const updatedMember = { ...selectedMember };

                // Actualizo la cuota que se ha actualizado.
                updatedMember.fees![indexFee] = {
                    ...updatedMember.fees![indexFee],
                    ...updatedFee,
                };

                // Busco el indice del miembro dentro del array de miembros anterior.
                const memberIndex = currentMembers.rows.findIndex(
                    (member: any) => member.id === updatedMember.id,
                );

                // Con el indice anterior sustituyo el miembro antiguo por el actualizado (ya tiene la cuota actualizada).
                currentMembers.rows[memberIndex] = {
                    ...currentMembers.rows[memberIndex],
                    ...updatedMember,
                };

                // Actualizo el objeto de miembros para pintarlo definitivamente en la vista.
                return currentMembers;
            },
        );
    }

    if (getGroupMembersQuery.isError) {
        return <SttError />;
    }

    const Pagination = () =>
        getGroupMembersQuery.isSuccess &&
        getGroupMembersQuery.data.count > 0 ? (
            <Box display="flex" justifyContent="flex-end">
                <SttPagination
                    page={getGroupMembersPage}
                    pages={getGroupMembersQuery.data.pages}
                    count={getGroupMembersQuery.data.count}
                    onChangePage={(page) => {
                        setGetGroupMembersPage(page);
                    }}
                />
            </Box>
        ) : null;

    return (
        <>
            <SttTopFilteringControls
                p={2}
                fields={[
                    {
                        type: 'search',
                        caption: t('generic.name'),
                        name: 'name',
                        value: filters.name || '',
                    },
                ]}
                onFilter={(newFilters: { name: string }) => {
                    onFilterChange({ ...filters, name: newFilters.name });
                    setGetGroupMembersPage(1);
                }}
            />

            {getGroupMembersQuery.isLoading && (
                <Box display="flex" justifyContent="center" paddingY={12}>
                    <CircularProgress />
                </Box>
            )}

            <Box mb={2}>
                <Pagination />
            </Box>

            <Box px={2}>
                {getGroupMembersQuery.isSuccess &&
                    getGroupMembersQuery.data.count > 0 &&
                    getGroupMembersQuery.data.rows.map(
                        (member: GroupMember, index: number) => (
                            <Paper
                                key={member.id}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <PersonOutlineOutlined
                                            fontSize="small"
                                            color="action"
                                        />
                                        <Link
                                            sx={{
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                            }}
                                            onClick={() => {
                                                setOpenMshipDialog(true);
                                                setSelectedMshipId(
                                                    member.user?.mship?.id,
                                                );
                                            }}
                                        >
                                            <Typography variant="subtitle1">
                                                {fullName(member.user)}
                                            </Typography>
                                        </Link>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        #
                                        {index +
                                            1 +
                                            (getGroupMembersPage - 1) * 25}
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 1.5 }} />
                                <SttFeeRow
                                    fees={member.fees as any}
                                    onFeeClicked={(id?: number) =>
                                        id && setIdPayModal(id)
                                    }
                                    onGenerated={(fee: any, indexFee: number) =>
                                        onFeeGenerated(fee, indexFee, member)
                                    }
                                />
                            </Paper>
                        ),
                    )}

                {getGroupMembersQuery.data?.count === 0 && <SttNoResults />}
            </Box>

            <Box mt={2} mb={2}>
                <Pagination />
            </Box>

            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onClose={() => {
                        setIdPayModal(null);
                    }}
                    onPay={updateFeeFromPayment}
                    onSave={updateFeeFromPayment}
                    onDeleted={deleteFeeFromPayment}
                    onRecovered={updateFeeFromPayment}
                    onRefund={deleteFeeFromPayment}
                />
            )}

            <SttMshipDialog
                open={openMshipDialog}
                mshipId={selectedMshipId}
                onClose={() => {
                    setOpenMshipDialog(false);
                }}
                onSave={() => {
                    cxt.showMessage('S', t('generic.saved'));
                }}
            />
        </>
    );
}
